/*
 * <book-appointment-cta> — VIS-445 / VIS-434.
 *
 * Wraps a regular button (snippets/button.liquid) and drives its destination
 * from the visitor's favorite store (localStorage `vo:favoriteStore`, owned by
 * store-favorites.js):
 *
 *  - No favorite: the anchor gets `data-store-selector-trigger`, so clicking it
 *    opens the store-selector popup (store-selector.js). The server-rendered
 *    locator href is the no-JS fallback.
 *  - Favorite set: the href swaps to that store's appointment link
 *    (favorite.appointmentUrl, the scheduler when known), or the manual booking
 *    form keyed by handle as a fallback. The trigger is removed so the click
 *    navigates straight there; absolute (external) URLs open in a new tab.
 *
 * Re-runs on `store-favorite:changed` so selecting/clearing the favorite updates
 * the button in place.
 *
 * VIS-1170: merchant-authored links opt in with the blocks' "Open the favorite
 * store's booking" setting, which renders `data-book-appointment` on the anchor
 * (button, card grid item, mega-menu banner, split hero slide, story). With a
 * favorite they get the same appointment link; without one they keep the link
 * the merchant set (usually the store locator page), no popup. This module is
 * loaded on every page by store-selector-modal.liquid.
 */

import { getFavorite, favoriteAppointmentUrl } from '@theme/store-favorites';

/**
 * Point a link at `url`, opening absolute (external) URLs in a new tab.
 * @param {HTMLAnchorElement} link
 * @param {string} url
 * @param {string} [target] - target to keep for same-site URLs
 */
function pointTo(link, url, target = '') {
  link.href = url;
  if (/^https?:\/\//i.test(url)) {
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  } else if (target) {
    link.target = target;
    link.rel = 'noopener noreferrer';
  } else {
    link.removeAttribute('target');
    link.removeAttribute('rel');
  }
}

/**
 * @param {HTMLAnchorElement} link
 * @param {string} appointmentUrl - '' when there is no favorite
 */
function reflectBookingLink(link, appointmentUrl) {
  // Remember the merchant's link the first time, to restore it when the
  // favorite is cleared.
  if (link.dataset.bookAppointmentHref == null) {
    link.dataset.bookAppointmentHref = link.getAttribute('href') || '';
    link.dataset.bookAppointmentTarget = link.getAttribute('target') || '';
  }
  if (appointmentUrl) {
    pointTo(link, appointmentUrl);
  } else {
    pointTo(link, link.dataset.bookAppointmentHref, link.dataset.bookAppointmentTarget);
  }
}

function reflectBookingLinks() {
  const appointmentUrl = favoriteAppointmentUrl(getFavorite());
  /** @type {NodeListOf<HTMLAnchorElement>} */
  const links = document.querySelectorAll('a[data-book-appointment]');
  for (const link of links) reflectBookingLink(link, appointmentUrl);
}

reflectBookingLinks();
window.addEventListener('store-favorite:changed', reflectBookingLinks);
// Back from the locator after picking a store: the bfcache page kept the old hrefs.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) reflectBookingLinks();
});
// Links inserted after load (the header's mega-menu copies) miss the pass
// above, so each link is also reflected on interaction: pointerover fixes the
// hover URL, pointerdown precedes click and the context menu, focusin covers
// keyboard, and capture on window runs before analytics.js reads the href.
for (const type of ['pointerover', 'pointerdown', 'focusin', 'click']) {
  window.addEventListener(
    type,
    (event) => {
      const link = event.target instanceof Element && event.target.closest('a[data-book-appointment]');
      if (link instanceof HTMLAnchorElement) reflectBookingLink(link, favoriteAppointmentUrl(getFavorite()));
    },
    true
  );
}

class BookAppointmentCta extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('a');
    this.locatorUrl = this.dataset.locatorUrl || '/pages/trova-negozio';

    window.addEventListener('store-favorite:changed', this.#reflect);
    this.#reflect();
  }

  disconnectedCallback() {
    window.removeEventListener('store-favorite:changed', this.#reflect);
  }

  #reflect = () => {
    if (!this.link) return;
    const favorite = getFavorite();
    // With a favorite, always route to its appointment link: the stored URL
    // (scheduler when known), or the manual booking form keyed by handle.
    const appointmentUrl = favoriteAppointmentUrl(favorite);

    if (appointmentUrl) {
      pointTo(this.link, appointmentUrl);
      // Navigate to the appointment link instead of opening the selector popup.
      this.link.removeAttribute('data-store-selector-trigger');
    } else {
      pointTo(this.link, this.locatorUrl);
      this.link.setAttribute('data-store-selector-trigger', '');
    }
  };
}

if (!customElements.get('book-appointment-cta')) {
  customElements.define('book-appointment-cta', BookAppointmentCta);
}
